import {
  BackEndValueType,
  ECardinality,
  ESymbolCategory,
  EWFParamType,
  IKType,
  ISymbol,
  IWFParameter
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { ParameterDefaultValueInput } from "../../right-panel/workflow-panel/wf-utils/parameter-default-value-input";
import {
  getDataTypeIcon,
  humanizeDataType,
  KuikaDataTypeDropdown
} from "../kuika-data-type-dropdown/kuika-data-type-dropdown";
import "./kuika-parameters-table.scss";

/**
 * The editable Process Parameters grid, driven by a single antd `Form.List`. It owns the
 * form instance and exposes `submit()` / `reset()` through a ref (see
 * `KuikaParametersTableHandle`) so the host card's CREATE / CANCEL buttons drive it. The
 * whole table is one form: CREATE validates every row (bar a pristine trailing draft) and
 * hands the collected rows to the host, which diffs them against the workflow and persists.
 */

/** Parameter names become C# identifiers, so ASCII letters only and no leading digit. */
const PARAMETER_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9]+$/;

interface ParameterTypeOption {
  value: boolean;
  label: string;
}

/** Which of the host's tabs the table is showing. */
export type ParameterScope = "all" | "businessObject" | "internal";

/**
 * A workflow parameter is either a Business Object (part of the process's business data)
 * or Internal (engine Bag state). The value maps to `isBusinessObject`; Input/Output are
 * separate booleans surfaced as their own columns, and the legacy `EWFParamType` role is
 * carried/derived, never user-picked.
 */
const PARAMETER_TYPE_OPTIONS: ParameterTypeOption[] = [
  { value: true, label: "Business Object" },
  { value: false, label: "Internal" }
];

interface HeaderCell {
  label: string;
  icon?: string;
}

const HEADER_CELLS: HeaderCell[] = [
  { label: "Parameter Name", icon: "components" },
  { label: "Data Type", icon: "code" },
  { label: "Array" },
  { label: "Parameter Type" },
  { label: "Input" },
  { label: "Output" },
  { label: "Default" }
];

/** The Data Type cell's value: a primitive OR a catalog type, tracked together. */
interface IParamTypeSelection {
  /** Backend primitive name; retained even for objects so a round-trip keeps a value. */
  dataType?: string;
  /** True when the type is a Kuika Data Type catalog entry. */
  isObject?: boolean;
  /** Catalog type name when `isObject`. */
  dataTypeName?: string;
}

/** One row as stored in the `Form.List`; flat scalars map cleanly to `Form.Item` fields. */
interface IParamFormRow {
  id: string;
  name: string;
  typeSelection: IParamTypeSelection;
  /** Array toggle; maps to `ECardinality.Many` / `.One`. */
  isArray: boolean;
  /** Legacy role carried through the round-trip; derived for new rows on save. */
  parameterType?: EWFParamType;
  isInput: boolean;
  isOutput: boolean;
  isBusinessObject: boolean;
  defaultValue?: ISymbol;
}

interface IParamsFormValues {
  params: IParamFormRow[];
}

export interface KuikaParametersTableHandle {
  submit: () => void;
  reset: () => void;
}

export interface KuikaParametersTableProps {
  parameters: IWFParameter[];
  types?: IKType[];
  /**
   * The host persists + propagates + reloads, then reports whether it saved. The table
   * resets itself (fresh draft) on `true` and keeps the user's edits on `false`.
   */
  onSubmit: (rows: IWFParameter[]) => Promise<boolean>;
  /** Fired when a data-type cell's "Manage Data Types" is clicked; the host opens the modal. */
  onManageDataTypes?: () => void;
  /** True while the data-type catalog is (re)loading; the in-cell dropdown shows a spinner. */
  dataTypesLoading?: boolean;
  /** Display filter from the host's tabs. */
  scope?: ParameterScope;
  search?: string;
}

/**
 * A parameter with no single fixed default (object / list) has no default-value cell.
 * Also heals loose data: a `defaultValue` that was persisted as a bare scalar (e.g. `true`
 * from the old Boolean-cell bug) is wrapped into a proper Fixed symbol so it re-saves as a
 * SymbolModel instead of re-persisting the bare value.
 */
const ensureDefaultSymbol = (symbol?: ISymbol): ISymbol => {
  if (symbol != null && typeof symbol === "object") return symbol;
  const bareValue: unknown = symbol;
  return {
    category: ESymbolCategory.fixed,
    label: "Fixed Value",
    description: bareValue == null ? "" : String(bareValue),
    fixedValue: bareValue ?? undefined
  } as ISymbol;
};

const toFormRow = (p: IWFParameter): IParamFormRow => ({
  id: p.id,
  name: p.name ?? "",
  typeSelection: { dataType: p.dataType, isObject: !!p.isObject, dataTypeName: p.dataTypeName },
  isArray: p.cardinality === ECardinality.Many,
  parameterType: p.parameterType,
  isInput: !!p.isInput,
  isOutput: !!p.isOutput,
  isBusinessObject: !!p.isBusinessObject,
  defaultValue: ensureDefaultSymbol(p.defaultValue)
});

/**
 * The legacy role must track the Input checkbox: the workflow-load normalizers recompute
 * `isInput` as `parameterType === Input` and discard the persisted boolean, so a stale
 * role silently reverts the checkbox on the next reload. A legacy `Output` role is
 * preserved for rows that never turn Input (some old consumers still fall back to it);
 * everything else non-input is the engine's Bag role.
 */
const deriveParameterType = (row: IParamFormRow): EWFParamType => {
  if (row.isInput) return EWFParamType.Input;
  if (row.parameterType === EWFParamType.Output) return EWFParamType.Output;
  return EWFParamType.Bag;
};

const toParameter = (row: IParamFormRow): IWFParameter => {
  const selection: IParamTypeSelection = row.typeSelection ?? {};
  const isObject: boolean = !!selection.isObject;
  return {
    id: row.id,
    name: (row.name ?? "").trim(),
    parameterType: deriveParameterType(row),
    isInput: row.isInput,
    isOutput: row.isOutput,
    isBusinessObject: row.isBusinessObject,
    cardinality: row.isArray ? ECardinality.Many : ECardinality.One,
    defaultValue: row.defaultValue,
    isObject,
    dataTypeName: isObject ? selection.dataTypeName : undefined,
    // The engine ignores `dataType` for objects, but the model requires a value.
    dataType: (selection.dataType ?? "String") as BackEndValueType
  };
};

/**
 * Parameter type answers itself: a scoped tab says which, and elsewhere Internal is the ordinary
 * case. It is a default, not a question, so it does not hold the row back — and it stays
 * editable on the row afterwards like every other cell.
 */
const draftParameterType = (scope: ParameterScope): boolean => scope === "businessObject";

/** The two a parameter cannot be built without. Everything else has a workable default. */
const isCompleteDraft = (row: IParamFormRow): boolean => {
  const selection: IParamTypeSelection = row.typeSelection ?? {};
  return !!(row.name ?? "").trim() && !!(selection.dataType || selection.dataTypeName);
};

const buildBlankDraft = (id: string, isBusinessObject: boolean): IParamFormRow => ({
  id,
  name: "",
  typeSelection: {},
  isArray: false,
  parameterType: undefined,
  isInput: false,
  isOutput: false,
  isBusinessObject,
  defaultValue: ensureDefaultSymbol(undefined)
});

/** A trailing draft the user never touched must not block CREATE. */
const isPristineDraft = (row: IParamFormRow): boolean => {
  const selection: IParamTypeSelection = row.typeSelection ?? {};
  return (
    !(row.name ?? "").trim() &&
    !selection.dataType &&
    !selection.dataTypeName &&
    !row.isArray &&
    !row.isInput &&
    !row.isOutput
  );
};

interface IValidateErrorField {
  name: (string | number)[];
  errors: string[];
}

interface IValidateError {
  errorFields?: IValidateErrorField[];
}

const firstValidationMessage = (error: unknown): string | undefined => {
  const typed: IValidateError = error as IValidateError;
  const field: IValidateErrorField | undefined = typed?.errorFields?.find(
    (f: IValidateErrorField): boolean => Array.isArray(f.errors) && f.errors.length > 0
  );
  return field?.errors?.[0];
};

interface DataTypeCellProps {
  value?: IParamTypeSelection;
  onChange?: (selection: IParamTypeSelection) => void;
  types?: IKType[];
  onManageDataTypes?: () => void;
  loading?: boolean;
  /** Array toggle for this row; when on, the trigger masks the type as `Type[]` (the model stays scalar). */
  isArray?: boolean;
}

/** Flush combobox trigger + popover hosting the reusable data-type dropdown. */
const DataTypeCell = ({
  value,
  onChange,
  types,
  onManageDataTypes,
  loading,
  isArray
}: DataTypeCellProps): JSX.Element => {
  const [visible, setVisible] = useState<boolean>(false);
  const selection: IParamTypeSelection = value ?? {};
  const isObject: boolean = !!selection.isObject;
  const currentName: string | undefined = isObject ? selection.dataTypeName : selection.dataType;
  const hasValue: boolean = !!currentName;

  const handleSelect = (dataType: string, isCatalogType: boolean): void => {
    if (isCatalogType) {
      onChange?.({ dataType: selection.dataType, isObject: true, dataTypeName: dataType });
    } else {
      onChange?.({ dataType, isObject: false, dataTypeName: undefined });
    }
  };

  return (
    <KuikaPopover
      visible={visible}
      onVisibleChange={setVisible}
      align="start"
      hideCloseIcon
      className="kuika-parameters-table__dropdown-popover"
      content={
        <KuikaDataTypeDropdown
          value={currentName}
          types={types}
          onManageDataTypes={onManageDataTypes}
          loading={loading}
          onChange={handleSelect}
          onRequestClose={(): void => setVisible(false)}
        />
      }
    >
      <button
        type="button"
        className={classNames("kuika-parameters-table__combo", {
          "kuika-parameters-table__combo--empty": !hasValue
        })}
        data-testid="processParameterDataTypeCell"
      >
        <span className="kuika-parameters-table__combo-value" data-testid="processParameterDataTypeText">
          <KuikaSvg name={getDataTypeIcon(currentName, isObject)} width={13} height={13} color="var(--dark-5)" />
          {hasValue ? `${humanizeDataType(currentName ?? "")}${isArray ? "[]" : ""}` : "Choose data type.."}
        </span>
        <KuikaSvg name={visible ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
      </button>
    </KuikaPopover>
  );
};

interface ParameterTypeCellProps {
  value?: boolean;
  onChange?: (value: boolean) => void;
}

/** Flush combobox trigger + popover with the fixed Business Object / Internal option list. */
const ParameterTypeCell = ({ value, onChange }: ParameterTypeCellProps): JSX.Element => {
  const [visible, setVisible] = useState<boolean>(false);
  const selected: ParameterTypeOption | undefined = PARAMETER_TYPE_OPTIONS.find(
    (o: ParameterTypeOption): boolean => o.value === value
  );

  const handleSelect = (next: boolean): void => {
    onChange?.(next);
    setVisible(false);
  };

  return (
    <KuikaPopover
      visible={visible}
      onVisibleChange={setVisible}
      align="start"
      hideCloseIcon
      className="kuika-parameters-table__dropdown-popover"
      content={
        <div className="kuika-parameters-table__option-list">
          {PARAMETER_TYPE_OPTIONS.map((o: ParameterTypeOption) => (
            <button
              key={o.label}
              type="button"
              className={classNames("kuika-parameters-table__option", {
                "kuika-parameters-table__option--selected": o.value === value
              })}
              data-testid={`processParameterTypeOption-${o.label.replace(/\s+/g, "").toLowerCase()}`}
              onClick={(): void => handleSelect(o.value)}
            >
              <span className="kuika-parameters-table__option-check">
                {o.value === value && <KuikaSvg name="check" width={12} height={12} color="var(--blue-5)" />}
              </span>
              {o.label}
            </button>
          ))}
        </div>
      }
    >
      <button
        type="button"
        className={classNames("kuika-parameters-table__combo", {
          "kuika-parameters-table__combo--empty": !selected
        })}
        data-testid="processParameterType"
      >
        <span className="kuika-parameters-table__combo-value">
          {selected ? selected.label : "Choose parameter type"}
        </span>
        <KuikaSvg name={visible ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
      </button>
    </KuikaPopover>
  );
};

interface DefaultValueCellProps {
  value?: ISymbol;
  onChange?: (value: ISymbol) => void;
  valueType?: BackEndValueType;
}

/**
 * Reuses the workflow default-value editor. Scalar inputs mutate `symbol.fixedValue` in
 * place; only the Boolean editor routes its value through `onValueChange` (as a raw
 * boolean). That boolean must be written INTO the symbol's `fixedValue` — never used to
 * replace the symbol itself, or the persisted `defaultValue` becomes a bare `true` instead
 * of a SymbolModel and backend deserialization of the workflow fails. A fresh symbol ref is
 * emitted so the antd Form registers the change.
 */
const DefaultValueCell = ({ value, onChange, valueType }: DefaultValueCellProps): JSX.Element => (
  <ParameterDefaultValueInput
    symbol={value}
    valueType={valueType}
    placeholder="Enter value"
    borderless
    onValueChange={(next: boolean): void => onChange?.({ ...ensureDefaultSymbol(value), fixedValue: next })}
  />
);

const KuikaParametersTable = forwardRef<KuikaParametersTableHandle, KuikaParametersTableProps>(
  (
    {
      parameters,
      types,
      onSubmit,
      onManageDataTypes,
      dataTypesLoading,
      scope = "all",
      search = ""
    }: KuikaParametersTableProps,
    ref
  ) => {
    const [form] = Form.useForm<IParamsFormValues>();
    const rootRef = useRef<HTMLDivElement>(null);
    // Read the freshest parameters after an async submit without capturing a stale closure.
    const parametersRef = useRef<IWFParameter[]>(parameters);
    parametersRef.current = parameters;

    const seedForm = (source: IWFParameter[]): void => {
      const rows: IParamFormRow[] = source.map(toFormRow);
      rows.push(buildBlankDraft(Guid.create().toString(), draftParameterType(scope)));
      form.setFieldsValue({ params: rows });
    };

    useEffect((): void => {
      seedForm(parameters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parameters]);

    // A new parameter authored on the Business Object tab is a business object; only the
    // trailing draft (always the last row) tracks the current scope.
    useEffect((): void => {
      const current: IParamFormRow[] | undefined = form.getFieldValue("params");
      if (!current || current.length === 0) return;
      const lastIndex: number = current.length - 1;
      form.setFields([{ name: ["params", lastIndex, "isBusinessObject"], value: draftParameterType(scope) }]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope]);

    /**
     * The next parameter starts where the last one did. Claimed over several frames: the data
     * type popover hands focus back to its own trigger as it unmounts, and that lands after the
     * first attempt — then released, so it can never fight the user for the field.
     */
    const focusDraftName = (framesLeft: number): void => {
      const draftRow: Element | null | undefined = rootRef.current?.querySelector(
        ".kuika-parameters-table__row--draft"
      );
      const node: Element | null | undefined = draftRow?.querySelector('[data-testid="processParameterName"]');
      const input: HTMLInputElement | null =
        node instanceof HTMLInputElement ? node : (node?.querySelector("input") ?? null);
      if (!input) return;
      if (document.activeElement !== input) input.focus();
      if (framesLeft > 0) requestAnimationFrame((): void => focusDraftName(framesLeft - 1));
    };

    /**
     * The trailing draft becomes a parameter as soon as it has a name and a data type, and a
     * fresh draft takes its place — there is no separate add step. Only user input reaches here,
     * so appending the blank row cannot feed itself.
     */
    const appendDraftWhenComplete = (moveFocus: boolean): void => {
      const rows: IParamFormRow[] | undefined = form.getFieldValue("params");
      if (!rows || rows.length === 0) return;
      if (!isCompleteDraft(rows[rows.length - 1])) return;
      form.setFieldsValue({
        params: [...rows, buildBlankDraft(Guid.create().toString(), draftParameterType(scope))]
      });
      // Only where the caret would otherwise be stranded — on Enter, and after a choice made in
      // the type popover, which hands focus back to its own trigger. A row that settled because
      // the field was left alone stays out of it: the user has already said where they are going.
      if (moveFocus) requestAnimationFrame((): void => focusDraftName(4));
    };

    /**
     * A name is typed a letter at a time and says nothing until the field is left, so it must
     * not settle the row on its own — the draft would split off after the first character. Every
     * other cell is a single discrete choice and can answer straight away.
     */
    const handleValuesChange = (changed: IParamsFormValues): void => {
      const rows: IParamFormRow[] | undefined = changed.params;
      // antd hands this a sparse array, so the index of the one filled slot is the row that
      // changed. The index matters: only the draft's own type may move the caret.
      const changedIndex: number = Array.isArray(rows) ? rows.findIndex((row: IParamFormRow): boolean => !!row) : -1;
      const changedRow: IParamFormRow | undefined = changedIndex >= 0 ? rows?.[changedIndex] : undefined;
      if (!changedRow) return;
      const keys: string[] = Object.keys(changedRow);
      if (keys.length === 1 && keys[0] === "name") return;
      appendDraftWhenComplete(false);
      // The data type is chosen in a popover, which hands focus back to its own trigger as it
      // closes — so whether that finished the row or only gave it a type, the caret goes to the
      // name, which is the next thing to say. Every other cell leaves it where the user put it.
      // An existing row's type is not that: pulling the caret to the trailing draft would scroll
      // the list away from the row being edited.
      const allRows: IParamFormRow[] = form.getFieldValue("params") ?? [];
      const isDraftRow: boolean = changedIndex === allRows.length - 1;
      if (isDraftRow && keys.includes("typeSelection")) requestAnimationFrame((): void => focusDraftName(4));
    };

    const submit = async (): Promise<void> => {
      const values: IParamsFormValues = form.getFieldsValue(true);
      const rows: IParamFormRow[] = values.params ?? [];
      if (rows.length === 0) {
        await onSubmit([]);
        return;
      }

      const lastIndex: number = rows.length - 1;
      const skipDraft: boolean = isPristineDraft(rows[lastIndex]);
      const keep = (i: number): boolean => !(i === lastIndex && skipDraft);

      const nameList: (string | number)[][] = [];
      rows.forEach((_r: IParamFormRow, i: number): void => {
        if (!keep(i)) return;
        nameList.push(["params", i, "name"]);
        nameList.push(["params", i, "typeSelection"]);
      });

      try {
        if (nameList.length > 0) await form.validateFields(nameList);
      } catch (error: unknown) {
        const message: string | undefined = firstValidationMessage(error);
        if (message) KMainFunctions.displayWarningNotification(message);
        return;
      }

      const collected: IWFParameter[] = rows
        .filter((_r: IParamFormRow, i: number): boolean => keep(i))
        .map(toParameter);
      const saved: boolean = await onSubmit(collected);
      if (saved) seedForm(parametersRef.current);
    };

    const reset = (): void => seedForm(parametersRef.current);

    useImperativeHandle(ref, (): KuikaParametersTableHandle => ({ submit: (): void => void submit(), reset }));

    const matchesFilters = (row: IParamFormRow, isDraft: boolean): boolean => {
      if (isDraft) return true;
      if (scope === "businessObject" && !row.isBusinessObject) return false;
      if (scope === "internal" && row.isBusinessObject) return false;
      const query: string = search.trim().toLowerCase();
      if (!query) return true;
      return (row.name ?? "").toLowerCase().includes(query);
    };

    return (
      <Form form={form} component={false} onValuesChange={handleValuesChange}>
        <div className="kuika-parameters-table" role="table" ref={rootRef}>
          <div className="kuika-parameters-table__head" role="row">
            {HEADER_CELLS.map((cell: HeaderCell) => (
              <div key={cell.label} className="kuika-parameters-table__head-cell" role="columnheader">
                {cell.icon && <KuikaSvg name={cell.icon} width={14} height={14} color="var(--dark-5)" />}
                {cell.label}
              </div>
            ))}
          </div>

          <Form.List name="params">
            {(fields, { remove }) => (
              <>
                {fields.map((field, index: number) => {
                  const isDraft: boolean = index === fields.length - 1;
                  const row: IParamFormRow = (form.getFieldValue(["params", field.name]) ?? {}) as IParamFormRow;
                  const isVisible: boolean = matchesFilters(row, isDraft);
                  return (
                    <div
                      key={field.key}
                      role="row"
                      className={classNames("kuika-parameters-table__row", {
                        "kuika-parameters-table__row--draft": isDraft,
                        "kuika-parameters-table__row--hidden": !isVisible
                      })}
                      data-testid={isDraft ? "processParameterDraftRow" : `processParameterRow-${row.id}`}
                    >
                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--flush">
                        <Form.Item
                          className="kuika-parameters-table__form-item"
                          name={[field.name, "name"]}
                          rules={[
                            { required: true, message: "Parameter name is required" },
                            { pattern: PARAMETER_NAME_PATTERN, message: "Field accepts only english letters." }
                          ]}
                        >
                          <KuikaInput
                            borderless
                            placeholder="Enter parameter name.."
                            data-testid="processParameterName"
                            onBlur={(): void => appendDraftWhenComplete(false)}
                            onPressEnter={(): void => appendDraftWhenComplete(true)}
                          />
                        </Form.Item>
                      </div>

                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--flush">
                        {/* Re-render the type trigger when this row's Array toggle flips, so the
                            label can mask the scalar as `Type[]` (antd 4.16 has no Form.useWatch). */}
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev: IParamsFormValues, cur: IParamsFormValues): boolean =>
                            prev?.params?.[field.name]?.isArray !== cur?.params?.[field.name]?.isArray
                          }
                        >
                          {(): JSX.Element => {
                            const isArray: boolean = !!form.getFieldValue(["params", field.name, "isArray"]);
                            return (
                              <Form.Item
                                className="kuika-parameters-table__form-item"
                                name={[field.name, "typeSelection"]}
                                rules={[
                                  {
                                    validator: (_rule: unknown, val: IParamTypeSelection): Promise<void> => {
                                      if (val && (val.dataType || val.dataTypeName)) return Promise.resolve();
                                      return Promise.reject(new Error("Data type is required"));
                                    }
                                  }
                                ]}
                              >
                                <DataTypeCell
                                  types={types}
                                  onManageDataTypes={onManageDataTypes}
                                  loading={dataTypesLoading}
                                  isArray={isArray}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </div>

                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--center">
                        <Form.Item
                          className="kuika-parameters-table__form-item"
                          name={[field.name, "isArray"]}
                          valuePropName="checked"
                          noStyle
                        >
                          <KuikaSwitch dataTestId="processParameterArray" />
                        </Form.Item>
                      </div>

                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--flush">
                        <Form.Item
                          className="kuika-parameters-table__form-item"
                          name={[field.name, "isBusinessObject"]}
                        >
                          <ParameterTypeCell />
                        </Form.Item>
                      </div>

                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--center">
                        <Form.Item
                          className="kuika-parameters-table__form-item"
                          name={[field.name, "isInput"]}
                          valuePropName="checked"
                          noStyle
                        >
                          <KuikaCheckbox dataTestId="processParameterInput" />
                        </Form.Item>
                      </div>

                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--center">
                        <Form.Item
                          className="kuika-parameters-table__form-item"
                          name={[field.name, "isOutput"]}
                          valuePropName="checked"
                          noStyle
                        >
                          <KuikaCheckbox dataTestId="processParameterOutput" />
                        </Form.Item>
                      </div>

                      <div className="kuika-parameters-table__cell kuika-parameters-table__cell--default">
                        <Form.Item noStyle shouldUpdate>
                          {(): JSX.Element | null => {
                            const current: IParamFormRow = (form.getFieldValue(["params", field.name]) ??
                              {}) as IParamFormRow;
                            const selection: IParamTypeSelection = current.typeSelection ?? {};
                            const isScalarSingle: boolean = !selection.isObject && !current.isArray;
                            if (!isScalarSingle) return null;
                            return (
                              <Form.Item
                                className="kuika-parameters-table__form-item"
                                name={[field.name, "defaultValue"]}
                                noStyle
                              >
                                <DefaultValueCell valueType={selection.dataType as BackEndValueType} />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                        {!isDraft && (
                          <KuikaTooltip content="Delete parameter" position="top">
                            <button
                              type="button"
                              className="kuika-parameters-table__remove"
                              aria-label={`Delete ${row.name}`}
                              data-testid="processParameterRemove"
                              onClick={(): void => remove(field.name)}
                            >
                              <KuikaSvg name="trash" width={14} height={14} color="var(--red-5)" />
                            </button>
                          </KuikaTooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </Form.List>
        </div>
      </Form>
    );
  }
);

KuikaParametersTable.displayName = "KuikaParametersTable";

export default KuikaParametersTable;
